import { useCallback, useEffect, useState } from 'react';
import { useLazyGetUsersQuery } from '@api/userList';

export type UserManagementTableRow = {
  id: string;
  companyName: string;
  user: string;
  role: string;
  internalUserAccessTemplateType: string;
  joiningDate: string;
  userStatus: string;
};

type UserListApiItem = {
  userId: string;
  email?: string;
  role?: string;
  userStatus?: string;
  joiningDate?: string;
  internalUserAccessTemplateType?: string;
  companyName?: string;
  companyId?: string;
};

export const mapUserListItem = (item: UserListApiItem): UserManagementTableRow => ({
  id: item.userId,
  companyName: item.companyName ?? '',
  user: item.email ?? '',
  role: item.role ?? '',
  internalUserAccessTemplateType: item.internalUserAccessTemplateType ?? '',
  joiningDate: item.joiningDate ?? '',
  userStatus: item.userStatus ?? '',
});

type UseUserManagementTableParams = {
  searchQuery: string;
  pageNumber: number;
  pageSize: number;
  onPageReset?: () => void;
};

export type UserManagementTableQuery = UseUserManagementTableParams;

export const useUserManagementTable = ({
  searchQuery,
  pageNumber,
  pageSize,
  onPageReset,
}: UseUserManagementTableParams) => {
  const [rows, setRows] = useState<UserManagementTableRow[]>([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('email');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [roleFilter, setRoleFilter] = useState<string>('');

  const [accessTemplateTypeFilter, setAccessTemplateTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [getUsers, { isLoading, isFetching }] = useLazyGetUsersQuery();

  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsers({
        pageNumber: pageNumber,
        pageSize,
        search: searchQuery,
        sort,
        sortOrder,
        role: roleFilter,
        internalUserAccessTemplateType: accessTemplateTypeFilter || undefined,
        userStatus: statusFilter ? [statusFilter] : undefined,
      }).unwrap();

      const items = response?.data?.items ?? [];
      setRows(items.map(mapUserListItem));
      setTotal(response?.data?.totalElements ?? 0);
    } catch (error) {
      console.error('Error while fetching users:', error);
      setRows([]);
      setTotal(0);
    }
  }, [
    getUsers,
    pageNumber,
    pageSize,
    searchQuery,
    sort,
    sortOrder,
    roleFilter,
    accessTemplateTypeFilter,
    statusFilter,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSort = useCallback(
    (sortKey: string) => {
      if (sortKey === sort) {
        setSortOrder((previous) => (previous === 'ASC' ? 'DESC' : 'ASC'));
      } else {
        setSort(sortKey);
        setSortOrder('ASC');
      }
      onPageReset?.();
    },
    [sort, onPageReset],
  );

  const handleRoleFilterChange = useCallback(
    (filter: string) => {
      setRoleFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  const handleAccessTemplateTypeFilterChange = useCallback(
    (filter: string) => {
      setAccessTemplateTypeFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  const handleStatusFilterChange = useCallback(
    (filter: string) => {
      setStatusFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  return {
    rows,
    isLoading,
    isFetching,
    total,
    refetch: fetchUsers,
    sort,
    sortOrder,
    handleSort,
    roleFilter,
    handleRoleFilterChange,
    accessTemplateTypeFilter,
    handleAccessTemplateTypeFilterChange,
    statusFilter,
    handleStatusFilterChange,
  };
};
